import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AwesomeQRCode } from '@awesomeqr/react';

import { createSecret, verifyToken } from 'api/tfa-api';
import { createAlert } from 'services/alert-service';
import { useCurrentUser } from 'shared-hooks/user';

import FieldDefault from 'components/field-default';

const TFAAppEnable = ({ email, authToken, additionalAuth, onSuccess, onEnd }) => {
    const { t } = useTranslation(['settings', 'common']);
    const [password, setPassword] = useState('');
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [authSecret, setAuthSecret] = useState(null);
    const [step, setStep] = useState('password');
    const currentUser = useCurrentUser();

    const userEmail = useMemo(() => {
        return email || currentUser?.email;
    }, [email, currentUser?.email])

    const handlePasswordSubmit = useCallback(async () => {
        setLoading(true);

        try {
            const response = await createSecret(password, authToken);
            if (response?.base32) {
                setAuthSecret(response?.base32);
                setStep('scan');
            } else {
                createAlert('error', 'Please try again later.');
            }
        } catch (e) {
            createAlert('error', e);
        }

        setLoading(false);
    }, [password, authToken]);

    const handleScanSubmit = () => {
        setStep('verify');
    }

    const handleVerifyBack = () => {
        setStep('scan');
    }

    const handleVerifySubmit = useCallback(async () => {
        setLoading(true);

        try {
            const response = await verifyToken(token, authToken);
            if (response?.success) {
                onSuccess && onSuccess();
                setStep('success');
            } else {
                createAlert('error', 'The token doesn\'t match');
            }

        } catch (e) {
            createAlert('error', e);
        }

        setLoading(false);
    }, [token, authToken]);

    const authURL = useMemo(() => {
        return `otpauth://totp/${userEmail}?secret=${authSecret}&digits=6&issuer=NiftyPM`;
    }, [userEmail, authSecret]);

    const userFriendlySecret = useMemo(() => {
        if (!authSecret) {
            return '';
        };

        return authSecret.match(/.{1,4}/g).join(' ');
    }, [authSecret]);

    if (step === 'password') {
        return (
            <div className="form">
                <div className="form-row">
                    <FieldDefault id="tfa-user-password" type="password" label={t('tfa.passwordStep.label')} placeholder={t('common:Password')} onChange={setPassword} passwordToggleable={true} />
                </div>

                <div className="form-row form-row-center">
                    <div className="btn-group btn-group-auto">
                        <button className="btn btn-secondary" onClick={onEnd}>{t('common:Cancel')}</button>

                        <button className={'btn btn-success btn-has-loading' + (loading ? ' loading' : '')} onClick={handlePasswordSubmit}>{t('common:Continue')}</button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'scan') {
        return (
            <div className="form">
                <div className="form-row">
                    <p className="form-row-text">{ t('tfa.scanStep.description') }</p>
                </div>

                <div className="form-row">
                    <div style={{ width: 200, height: 200, margin: '0 auto' }}>
                        <AwesomeQRCode options={{ text: authURL }} />
                    </div>
                </div>

                <div className="form-row">
                    <p className="form-row-text">
                        { t('tfa.scanStep.enterCode') }
                    </p>

                    <p className="form-row-text">
                        <code>
                            { userFriendlySecret }
                        </code>
                    </p>
                </div>

                <div className="form-row form-row-center">
                    <button className="btn btn-success" onClick={handleScanSubmit}>{t('common:Continue')}</button>
                </div>
            </div>
        );
    }

    if (step === 'verify') {
        return (
            <div className="form">
                <div className="form-row">
                    <p className="form-row-text">{ t('tfa.verifyStep.description') }</p>
                    <p className="form-row-text"></p>
                </div>

                <div className="form-row">
                    <FieldDefault id="tfa-user-password" type="number" label={ t('tfa.tokenLabel') } placeholder={ t('tfa.tokenPlaceholder') } onChange={setToken} />
                </div>

                <div className="form-row form-row-center">
                    <div className="btn-group btn-group-auto">
                        <button className="btn btn-secondary" onClick={handleVerifyBack}>{t('common:Go Back')}</button>

                        <button className={'btn btn-success btn-has-loading' + (loading ? ' loading' : '')} onClick={handleVerifySubmit}>{t('common:Continue')}</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="form">
            <div className="form-row">
                <h3 className="form-row-text">{ t('tfa.successStep.title') } 🎉</h3>
            </div>

            <div className="form-row form-row-center">
                <button className="btn btn-success" onClick={onEnd}>{t('common:Close')}</button>
            </div>
        </div>
    )
}

export default TFAAppEnable;
